import { clsx } from 'clsx';
import { EyeOff, Search } from 'lucide-react';
import { useCreativeStrategyStore } from '@/stores/creativeStrategy';
import type { CreativeStrategyItem } from '@/services/api';

const LEVELS = [
  { value: '', label: 'All levels' },
  { value: 'client', label: 'Client' },
  { value: 'niche', label: 'Niche' },
  { value: 'category', label: 'Category' },
  { value: 'network', label: 'Network' },
];

function metric(item: CreativeStrategyItem): string {
  if (item.entity_class === 'market_concept') return `${item.support ?? 0} reviews`;
  if (item.entity_class === 'creative_strategy') return `${item.advertisers ?? 0} advertisers · ${item.support ?? 0} ads`;
  return `${item.advertisers ?? 0} brands · ${item.support ?? 0} ads`;
}

function subtitle(item: CreativeStrategyItem): string {
  const d = item.data as Record<string, string | undefined>;
  if (item.entity_class === 'market_concept') return d.decision_factor ?? d.concept_type ?? '';
  if (item.entity_class === 'creative_strategy') return d.argument ?? '';
  return d.structure ?? '';
}

export function PackItemList({ onSelect }: { onSelect: (id: string | null) => void }) {
  const { filters, setFilters, selectedItemId, visibleItems, pack } = useCreativeStrategyStore();
  const items = visibleItems();

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="p-3 space-y-2 border-b border-slate-100">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            id="cs-item-search"
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            placeholder="Search name, evidence, structure…"
            className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg pl-8 pr-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            id="cs-item-level"
            value={filters.level}
            onChange={(e) => setFilters({ level: e.target.value })}
            className="flex-1 bg-slate-50 text-xs text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:outline-none"
          >
            {LEVELS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
            <input
              id="cs-show-hidden"
              type="checkbox"
              checked={filters.showHidden}
              onChange={(e) => setFilters({ showHidden: e.target.checked })}
            />
            show hidden
          </label>
        </div>
        <div className="text-[11px] text-slate-400">
          {items.length} shown{pack && pack.hidden_count > 0 ? ` · ${pack.hidden_count} hidden in this docket` : ''}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">Nothing matches</div>
        ) : (
          items.map((item) => {
            const active = item.resolved_id === selectedItemId;
            const levels = ((item.data.levels as string[] | undefined) ?? []).join(' · ');
            return (
              <button
                key={item.resolved_id}
                onClick={() => onSelect(item.resolved_id)}
                className={clsx(
                  'w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors',
                  active && 'bg-brand-teal/5 border-l-2 border-l-brand-teal',
                  item.hidden && 'opacity-60',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-slate-400">{item.resolved_id}</span>
                      {item.hidden && <EyeOff size={11} className="text-amber-600" />}
                      {item.client_runs_it === false && (
                        <span className="text-[9px] uppercase tracking-wide text-brand-teal font-semibold">not yet run</span>
                      )}
                      {item.in_client_graph && (
                        <span className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">in graph</span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-navy-900 truncate">{item.canonical_name}</div>
                    <div className="text-xs text-slate-500 line-clamp-2">{subtitle(item)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[11px] text-slate-600 tabular-nums whitespace-nowrap">{metric(item)}</div>
                    {levels && <div className="text-[10px] text-slate-400">{levels}</div>}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
